/**
 * Object keys in blob storage.
 * Layout: `{org}/{kind}/{id}.{ext}` for covers,
 * `{org}/{kind}/{projectId}/{underlayId}.jpg` for PDF underlays,
 * `{org}/project-file/{batchId}/{fileId}.{ext}` for pipeline originals,
 * `{org}/project-file-crop/{batchId}/{drawingId}.jpg` for legacy per-drawing images,
 * `{org}/project-file-pages/{batchId}/{fileId}/{pageIndex}.webp` for a page raster,
 * `{org}/{kind}/{fileId}` for uploaded files.
 */

export const PDF_UNDERLAY_BLOB_KIND = 'pdf-underlay' as const;
export const REPORT_COVER_BLOB_KIND = 'report-cover' as const;
export const PROJECT_FILE_BLOB_KIND = 'project-file' as const;
export const PROJECT_FILE_CROP_BLOB_KIND = 'project-file-crop' as const;
export const PROJECT_FILE_PAGE_RASTER_BLOB_KIND = 'project-file-pages' as const;
export const UPLOADED_FILE_BLOB_KIND = 'uploaded-file' as const;

export type BlobKind =
  | typeof PDF_UNDERLAY_BLOB_KIND
  | typeof REPORT_COVER_BLOB_KIND
  | typeof PROJECT_FILE_BLOB_KIND
  | typeof PROJECT_FILE_CROP_BLOB_KIND
  | typeof PROJECT_FILE_PAGE_RASTER_BLOB_KIND
  | typeof UPLOADED_FILE_BLOB_KIND;

const UUID_IN_PATH =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const PROJECT_ID_IN_PATH = /^\d+$/;

const IMAGE_MIME_TO_EXT = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
} as const;

const PROJECT_FILE_MIME_TO_EXT: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'application/pdf': 'pdf',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'docx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'text/csv': 'csv',
};

const IMAGE_EXT_TO_MIME: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};

const PROJECT_FILE_EXT_TO_MIME: Record<string, string> = {
  ...IMAGE_EXT_TO_MIME,
  gif: 'image/gif',
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv',
  bin: 'application/octet-stream',
};

const PROJECT_FILE_EXTS = new Set(Object.keys(PROJECT_FILE_EXT_TO_MIME));

/**
 * Collapse path separators so an org id cannot escape its directory.
 */
export const sanitizeBlobOrgSegment = (organization: string): string => {
  const trimmed = organization.trim();
  if (!trimmed) return 'unknown-org';
  return trimmed.replace(/[/\\]+/g, '_');
};

/**
 * File extension for a stored image MIME type (`jpg` for JPEG).
 * Unknown MIME types throw — callers must pass an allow-listed type.
 */
export const blobExtensionForMimeType = (mimeType: string): string => {
  if (mimeType === 'image/jpeg') return IMAGE_MIME_TO_EXT['image/jpeg'];
  if (mimeType === 'image/png') return IMAGE_MIME_TO_EXT['image/png'];
  if (mimeType === 'image/webp') return IMAGE_MIME_TO_EXT['image/webp'];
  throw new Error(`Unsupported blob image MIME type: ${mimeType}`);
};

/**
 * File extension for a project-file original stored in blob storage.
 * Unknown MIME types fall back to `bin`.
 */
export const blobExtensionForProjectFileMimeType = (
  mimeType: string,
  fileName: string,
): string => {
  const fromMime = PROJECT_FILE_MIME_TO_EXT[mimeType.toLowerCase()];
  if (fromMime) return fromMime;
  const dot = fileName.lastIndexOf('.');
  if (dot >= 0) {
    const ext = fileName.slice(dot + 1).toLowerCase();
    if (PROJECT_FILE_EXTS.has(ext)) return ext;
  }
  return 'bin';
};

/**
 * MIME type inferred from a blob filename extension.
 */
export const mimeTypeFromBlobFileName = (fileName: string): string => {
  const ext = fileName.includes('.')
    ? fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase()
    : '';
  return (
    PROJECT_FILE_EXT_TO_MIME[ext] ??
    IMAGE_EXT_TO_MIME[ext] ??
    'application/octet-stream'
  );
};

const isSafeOrgSegment = (org: string | undefined): org is string => {
  if (!org) return false;
  return org !== '.' && org !== '..' && !org.includes('..');
};

/**
 * Build the blob key for a PDF underlay raster.
 * Layout: `{org}/{kind}/{projectId}/{underlayId}.jpg` so keys cannot collide
 * across projects in the same organization.
 */
export const buildPdfUnderlayBlobRef = (
  organization: string,
  projectId: number,
  underlayId: string,
): string =>
  `${sanitizeBlobOrgSegment(organization)}/${PDF_UNDERLAY_BLOB_KIND}/${projectId}/${underlayId}.jpg`;

/**
 * Build the blob key for a report cover still.
 */
export const buildReportCoverBlobRef = (
  organization: string,
  projectId: number,
  mimeType: string,
): string =>
  `${sanitizeBlobOrgSegment(organization)}/${REPORT_COVER_BLOB_KIND}/${projectId}.${blobExtensionForMimeType(mimeType)}`;

/**
 * Build the blob key for an uploaded file (`uploaded_files.blob_ref`). The key
 * has no extension because uploads can be any type; the MIME type is stored on
 * the row.
 */
export const buildUploadedFileBlobRef = (
  organization: string,
  fileId: string,
): string =>
  `${sanitizeBlobOrgSegment(organization)}/${UPLOADED_FILE_BLOB_KIND}/${fileId}`;

/** True when {@link blobRef} is a well-formed `{org}/uploaded-file/{fileId}` key. */
export const isUploadedFileBlobRef = (blobRef: string): boolean => {
  const parts = blobRef.split('/');
  if (parts.length !== 3) return false;
  const [org, kind, fileId] = parts;
  return (
    isSafeOrgSegment(org) &&
    kind === UPLOADED_FILE_BLOB_KIND &&
    UUID_IN_PATH.test(fileId ?? '')
  );
};

export type ParsedPdfUnderlayBlobRef = {
  organization: string;
  projectId: number;
  underlayId: string;
};

/**
 * Parse a PDF underlay object key, or `undefined` when the string is not a
 * well-formed `{org}/pdf-underlay/{projectId}/{underlayId}.jpg` ref.
 */
export const parsePdfUnderlayBlobRef = (
  blobRef: string,
): ParsedPdfUnderlayBlobRef | undefined => {
  const parts = blobRef.split('/');
  if (parts.length !== 4) return undefined;
  const [org, kind, projectId, fileName] = parts;
  if (
    !isSafeOrgSegment(org) ||
    kind !== PDF_UNDERLAY_BLOB_KIND ||
    !projectId ||
    !PROJECT_ID_IN_PATH.test(projectId) ||
    !fileName ||
    !fileName.endsWith('.jpg')
  ) {
    return undefined;
  }
  const underlayId = fileName.slice(0, -'.jpg'.length);
  if (!UUID_IN_PATH.test(underlayId)) return undefined;
  return { organization: org, projectId: Number(projectId), underlayId };
};

/**
 * True when {@link blobRef} is a well-formed PDF underlay object key.
 */
export const isPdfUnderlayBlobRef = (blobRef: string): boolean =>
  Boolean(parsePdfUnderlayBlobRef(blobRef));

/**
 * True when {@link blobRef} is a well-formed report cover object key.
 */
export const isReportCoverBlobRef = (blobRef: string): boolean => {
  const parts = blobRef.split('/');
  if (parts.length !== 3) return false;
  const [org, kind, fileName] = parts;
  if (!isSafeOrgSegment(org) || kind !== REPORT_COVER_BLOB_KIND || !fileName) {
    return false;
  }
  const dot = fileName.lastIndexOf('.');
  if (dot < 1) return false;
  const id = fileName.slice(0, dot);
  const ext = fileName.slice(dot + 1).toLowerCase();
  return PROJECT_ID_IN_PATH.test(id) && Boolean(IMAGE_EXT_TO_MIME[ext]);
};

/**
 * Build the blob key for an uploaded project-file original.
 * Layout: `{org}/project-file/{batchId}/{fileId}.{ext}` — batch id is used
 * because `project_id` may still be null when the file is uploaded.
 */
export const buildProjectFileBlobRef = (
  organization: string,
  batchId: string,
  fileId: string,
  mimeType: string,
  fileName: string,
): string => {
  const ext = blobExtensionForProjectFileMimeType(mimeType, fileName);
  return `${sanitizeBlobOrgSegment(organization)}/${PROJECT_FILE_BLOB_KIND}/${batchId}/${fileId}.${ext}`;
};

/**
 * Build the blob key for a cropped drawing JPEG.
 */
export const buildProjectFileCropBlobRef = (
  organization: string,
  batchId: string,
  drawingId: string,
): string =>
  `${sanitizeBlobOrgSegment(organization)}/${PROJECT_FILE_CROP_BLOB_KIND}/${batchId}/${drawingId}.jpg`;

export type ParsedProjectFileBlobRef = {
  organization: string;
  batchId: string;
  fileId: string;
};

export type ParsedProjectFileCropBlobRef = {
  organization: string;
  batchId: string;
  drawingId: string;
};

/**
 * Parse a project-file original object key.
 */
export const parseProjectFileBlobRef = (
  blobRef: string,
): ParsedProjectFileBlobRef | undefined => {
  const parts = blobRef.split('/');
  if (parts.length !== 4) return undefined;
  const [org, kind, batchId, fileName] = parts;
  if (
    !isSafeOrgSegment(org) ||
    kind !== PROJECT_FILE_BLOB_KIND ||
    !batchId ||
    !UUID_IN_PATH.test(batchId) ||
    !fileName
  ) {
    return undefined;
  }
  const dot = fileName.lastIndexOf('.');
  if (dot < 1) return undefined;
  const fileId = fileName.slice(0, dot);
  const ext = fileName.slice(dot + 1).toLowerCase();
  if (!UUID_IN_PATH.test(fileId) || !PROJECT_FILE_EXTS.has(ext)) {
    return undefined;
  }
  return { organization: org, batchId, fileId };
};

/**
 * Parse a cropped-drawing object key.
 */
export const parseProjectFileCropBlobRef = (
  blobRef: string,
): ParsedProjectFileCropBlobRef | undefined => {
  const parts = blobRef.split('/');
  if (parts.length !== 4) return undefined;
  const [org, kind, batchId, fileName] = parts;
  if (
    !isSafeOrgSegment(org) ||
    kind !== PROJECT_FILE_CROP_BLOB_KIND ||
    !batchId ||
    !UUID_IN_PATH.test(batchId) ||
    !fileName ||
    !fileName.endsWith('.jpg')
  ) {
    return undefined;
  }
  const drawingId = fileName.slice(0, -'.jpg'.length);
  if (!UUID_IN_PATH.test(drawingId)) return undefined;
  return { organization: org, batchId, drawingId };
};

export const isProjectFileBlobRef = (blobRef: string): boolean =>
  Boolean(parseProjectFileBlobRef(blobRef));

export const isProjectFileCropBlobRef = (blobRef: string): boolean =>
  Boolean(parseProjectFileCropBlobRef(blobRef));

export type ProjectFilePageRasterFormat = 'webp' | 'jpeg' | 'png';

const PAGE_RASTER_EXTENSION: Record<ProjectFilePageRasterFormat, string> = {
  webp: 'webp',
  jpeg: 'jpg',
  png: 'png',
};

const formatForPageRasterExt = (
  ext: string,
): ProjectFilePageRasterFormat | null => {
  switch (ext) {
    case 'webp':
      return 'webp';
    case 'jpg':
      return 'jpeg';
    case 'png':
      return 'png';
    default:
      return null;
  }
};

/**
 * Blob key for one page raster.
 * Layout: `{org}/project-file-pages/{batchId}/{fileId}/{pageIndex}.{ext}`.
 */
export const buildProjectFilePageRasterBlobRef = (
  organization: string,
  batchId: string,
  fileId: string,
  pageIndex: number,
  format: ProjectFilePageRasterFormat,
): string =>
  `${sanitizeBlobOrgSegment(organization)}/${PROJECT_FILE_PAGE_RASTER_BLOB_KIND}/${batchId}/${fileId}/${pageIndex}.${PAGE_RASTER_EXTENSION[format]}`;

export type ParsedProjectFilePageRasterBlobRef = {
  organization: string;
  batchId: string;
  fileId: string;
  pageIndex: number;
  format: ProjectFilePageRasterFormat;
};

/** Parse a page-raster object key. */
export const parseProjectFilePageRasterBlobRef = (
  blobRef: string,
): ParsedProjectFilePageRasterBlobRef | undefined => {
  const parts = blobRef.split('/');
  if (parts.length !== 5) return undefined;
  const [org, kind, batchId, fileId, fileName] = parts;
  if (
    !isSafeOrgSegment(org) ||
    kind !== PROJECT_FILE_PAGE_RASTER_BLOB_KIND ||
    !batchId ||
    !UUID_IN_PATH.test(batchId) ||
    !fileId ||
    !UUID_IN_PATH.test(fileId) ||
    !fileName
  ) {
    return undefined;
  }
  const dot = fileName.lastIndexOf('.');
  if (dot < 1) return undefined;
  const pageText = fileName.slice(0, dot);
  const ext = fileName.slice(dot + 1).toLowerCase();
  if (!/^\d+$/.test(pageText)) return undefined;
  const format = formatForPageRasterExt(ext);
  if (format == null) return undefined;
  return {
    organization: org,
    batchId,
    fileId,
    pageIndex: Number(pageText),
    format,
  };
};

export const isProjectFilePageRasterBlobRef = (blobRef: string): boolean =>
  Boolean(parseProjectFilePageRasterBlobRef(blobRef));

/**
 * True when {@link blobRef} is an allowed object key (no traversal).
 */
export const isSafeBlobRef = (blobRef: string): boolean =>
  isPdfUnderlayBlobRef(blobRef) ||
  isReportCoverBlobRef(blobRef) ||
  isProjectFileBlobRef(blobRef) ||
  isProjectFileCropBlobRef(blobRef) ||
  isProjectFilePageRasterBlobRef(blobRef) ||
  isUploadedFileBlobRef(blobRef);

/**
 * True when {@link blobRef} belongs to {@link organization}.
 */
export const blobRefMatchesOrg = (
  blobRef: string,
  organization: string,
): boolean => {
  if (!isSafeBlobRef(blobRef)) return false;
  return blobRef.startsWith(`${sanitizeBlobOrgSegment(organization)}/`);
};

/**
 * True when a PDF underlay {@link blobRef} belongs to {@link organization}.
 */
export const pdfUnderlayBlobRefMatchesOrg = (
  blobRef: string,
  organization: string,
): boolean =>
  isPdfUnderlayBlobRef(blobRef) && blobRefMatchesOrg(blobRef, organization);

/**
 * True when {@link blobRef} is the canonical key for this org, project, and
 * underlay — not merely another object in the same organization.
 */
export const pdfUnderlayBlobRefMatchesProject = (
  blobRef: string,
  organization: string,
  projectId: number,
  underlayId: string,
): boolean =>
  blobRef === buildPdfUnderlayBlobRef(organization, projectId, underlayId);
